<template>
  <div class="rd-screen">
    <report-tools :copy="takenCompanies.length > 0" @copy="copyTable">
      <label class="rd-live" :class="{ 'is-on': live }">
        <span class="rd-live__dot"></span>
        <input
          v-model="live"
          type="checkbox"
          class="rd-switch"
          role="switch"
          :aria-label="$t('report_desktop.live')"
        />
        <span class="rd-num">{{ liveText }}</span>
      </label>
    </report-tools>

    <report-state v-if="failed && !takenCompanies.length" kind="error" @retry="getTotalShiftMeals()"></report-state>
    <report-state v-else-if="loading && !report.length" kind="loading"></report-state>
    <report-state
      v-else-if="!takenCompanies.length"
      icon="mdi-truck-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="takenCompanies"
      :row-key="(company) => company.key"
      :totals="footTotals"
      :total-label="$t('total')"
      :selected="isOpen"
    >
      <template #cell-shift="{ row, column }">
        <span
          v-if="cell(row, column.shift).ordered"
          class="rd-cellbar"
          :class="{ 'is-complete': cell(row, column.shift).complete }"
        >
          <span class="rd-cellbar__text">
            {{ cell(row, column.shift).taken }} / {{ cell(row, column.shift).ordered }}
          </span>
          <span class="rd-cellbar__bar"><i :style="{ width: `${cell(row, column.shift).percent}%` }"></i></span>
        </span>
        <span v-else class="is-zero">–</span>
      </template>

      <template #cell-total="{ row }">
        <b>{{ row.taken }}</b> / {{ row.ordered }}
        <span class="rd-mini" :class="{ 'is-complete': row.complete }">
          <i :style="{ width: `${row.percent}%` }"></i>
        </span>
      </template>

      <template #cell-actions="{ row }">
        <div class="rd-tbl__actions">
          <button type="button" class="ui-btn ui-btn--sm" :aria-label="$t('employees')" @click="openWorkers(row, $event)">
            <v-icon size="16">mdi-account-multiple-outline</v-icon>
            <span class="rd-btn__label">{{ $t("employees") }}</span>
          </button>
          <button
            type="button"
            class="ui-btn ui-btn--sm"
            :aria-label="$t('monthly')"
            @click="openDialogReportFirm(row.source)"
          >
            <v-icon size="16">mdi-calendar-month-outline</v-icon>
            <span class="rd-btn__label">{{ $t("monthly") }}</span>
          </button>
        </div>
      </template>
    </ui-table>

    <report-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :company-data="firmReportItem"
    ></report-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsTabTaken from "@/components/report/meals/tabs/ReportMealsTabTaken.vue";
import { SHIFTS } from "@/constants/shifts";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import hasTakenCompanies from "../../shared/hasTakenCompanies";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import ReportDesktopEmployees from "./ReportDesktopEmployees.vue";
import { copyText, rowsToTsv } from "../copyTable";

const LIVE_MS = 30000;
const TICK_MS = 5000;

/**
 * Preuzeto na računaru: firma × smena (preuzeto / poručeno sa trakom napretka), ukupno,
 * "Radnici" (panel) i "Mesečno" (dijalog kao do sada). Prekidač "Uživo" osvežava na 30 s.
 * Nasleđuje učitavanje, štampu i Excel iz ReportMealsTabTaken.
 */
export default {
  name: "ReportDesktopTaken",
  extends: ReportMealsTabTaken,
  mixins: [hasTakenCompanies, hasShiftBinding, hasShiftSubtitle],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    failed: false,
    live: false,
    refreshedAt: 0,
    now: Date.now(),
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "name", label: this.$t("firm"), name: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, label: this.$t(shift.label), slot: "shift", shift, align: "end" })),
        { key: "total", label: this.$t("total"), align: "end", num: true },
        { key: "actions", label: "", align: "end", headCls: "rd-col-actions" },
      ];
    },
    /** Zbirni red (tekst): preuzeto / poručeno po smeni i ukupno. */
    footTotals() {
      const totals = { total: `${this.summary.taken} / ${this.summary.ordered}` };
      this.visibleShifts.forEach((shift) => {
        const sum = this.shiftTotal(shift);
        totals[shift.key] = sum.ordered ? `${sum.taken} / ${sum.ordered}` : "–";
      });
      return totals;
    },
    /** Smene 1–3 uvek, četvrta samo kad neka firma ima obroke u njoj. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) && (shift.id < 4 || this.shiftHasOrders(shift))
      );
    },
    liveText() {
      if (!this.live) {
        return this.$t("report_desktop.live_off");
      }
      const seconds = Math.max(0, Math.round((this.now - this.refreshedAt) / 1000));
      return this.$t("report_desktop.live_on", { s: seconds });
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_taken_meals_data");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        {
          key: "taken",
          label: this.$t("taken"),
          value: this.summary.taken,
          of: this.summary.ordered,
          percent: this.summary.percent,
          tone: "inverse",
        },
        { key: "companies", label: this.$t("companies"), value: this.summary.companies },
        {
          key: "left",
          label: this.$t("report_desktop.remaining"),
          value: Math.max(0, this.summary.ordered - this.summary.taken),
          tone: "accent",
        },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.takenCompanies.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
    live(on) {
      if (on) {
        this.startLive();
      } else {
        this.stopLive();
      }
    },
  },
  beforeDestroy() {
    this.stopLive();
  },
  methods: {
    /** Isto kao stari tab, uz stanje greške i "tiho" osvežavanje (bez skeleta) za uživo. */
    getTotalShiftMeals({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
      }
      this.failed = false;
      return reportService
        .getTotalShiftMeals(this.$store.state.restaurant.id, {
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        })
        .then((res) => {
          this.report = res.data;
          this.refreshedAt = Date.now();
          this.now = this.refreshedAt;
        })
        .catch((error) => {
          console.error("Error loading total shift meals:", error);
          this.failed = true;
          if (!silent) {
            this.report = [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startLive() {
      this.refreshedAt = Date.now();
      this.now = this.refreshedAt;
      this.liveTimer = setInterval(() => {
        if (!document.hidden) {
          this.getTotalShiftMeals({ silent: true });
        }
      }, LIVE_MS);
      this.tickTimer = setInterval(() => {
        this.now = Date.now();
      }, TICK_MS);
    },
    stopLive() {
      clearInterval(this.liveTimer);
      clearInterval(this.tickTimer);
    },
    shiftHasOrders(shift) {
      return this.takenCompanies.some((company) => this.parseTaken(company.source[shift.key]).ordered > 0);
    },
    cell(company, shift) {
      const { taken, ordered } = this.parseTaken(company.source[shift.key]);
      return {
        taken,
        ordered,
        percent: this.percentOf(taken, ordered),
        complete: ordered > 0 && taken >= ordered,
      };
    },
    shiftTotal(shift) {
      return this.takenCompanies.reduce(
        (total, company) => {
          const { taken, ordered } = this.parseTaken(company.source[shift.key]);
          return { taken: total.taken + taken, ordered: total.ordered + ordered };
        },
        { taken: 0, ordered: 0 }
      );
    },
    isOpen(company) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === this.workersKey(company));
    },
    workersKey(company) {
      return `workers-${company.source.id}`;
    },
    /** "Radnici": lista radnika firme u panelu (klik na radnika otvara njegov mesečni pregled). */
    openWorkers(company, event) {
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: this.workersKey(company),
        root: true,
        title: company.name,
        subtitle: this.$t("employees"),
        component: ReportDesktopEmployees,
        props: { companyOverride: { id: company.source.id, name: company.name }, embedded: true },
      });
    },
    async copyTable() {
      const headers = [
        this.$t("firm"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.takenCompanies.map((company) => [
        company.name,
        ...this.visibleShifts.map((shift) => {
          const { taken, ordered } = this.parseTaken(company.source[shift.key]);
          return ordered ? `${taken}/${ordered}` : "";
        }),
        `${company.taken}/${company.ordered}`,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
